import React, {useCallback, useContext, useEffect, useState} from "react";
import {Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField,} from "@mui/material";
import {useMutation} from "react-query";
import {useNavigate} from "react-router-dom";

import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import {createUser} from "../../../api/userApi";
import {LanguageContext} from "../../../components/LanguageContext";
import {isAdminOrOwner, isOwner} from "../../../utils/permissions";

const CreateUserPage = () => {
    const {translations} = useContext(LanguageContext) || {};
    const navigate = useNavigate();
    const canAccess = isAdminOrOwner();
    const owner = isOwner();

    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [type, setType] = useState("A");

    useEffect(() => {
        if (!canAccess) {
            navigate("/korisnici", {replace: true});
        }
    }, [canAccess, navigate]);

    const mutation = useMutation({
        mutationFn: (formData) => createUser(formData),
        onSuccess: () => {
            setError(null);
            setSuccess(
                translations?.adminCreateUserPageSuccessMessage ||
                "Korisnik je uspešno kreiran!"
            );
            setTimeout(() => navigate("/korisnici"), 1500);
        },
        onError: (err) => {
            setSuccess(null);
            setError(
                err.response?.data?.message ||
                translations?.adminCreateUserPageErrorMessage ||
                "Došlo je do greške prilikom kreiranja korisnika."
            );
        },
    });

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);

            if (!formData.get("name")) {
                setError(
                    translations?.adminCreateUserPageNameRequired ||
                    "Ime je obavezno!"
                );
                return;
            }
            if (!formData.get("surname")) {
                setError(
                    translations?.adminCreateUserPageSurnameRequired ||
                    "Prezime je obavezno!"
                );
                return;
            }
            if (!formData.get("email")) {
                setError(
                    translations?.adminCreateUserPageEmailRequired ||
                    "Email je obavezan!"
                );
                return;
            }

            formData.append("type", type);
            mutation.mutateAsync(formData).catch(() => {
            });
        },
        [translations, mutation, type]
    );

    return (
        <Box>
            <PageHeader
                icon={<PersonAddAlt1OutlinedIcon/>}
                eyebrow={translations?.adminAdministratorMenuUsers || "Users"}
                title={
                    translations?.adminCreateUserPageAddButton || "Novi korisnik"
                }
                description={
                    translations?.createUserSubtitle ||
                    "Kreirajte novi nalog. Lozinka se generiše automatski i šalje na e-mail."
                }
            />

            <Stack spacing={2}>
                {success && (
                    <Alert severity="success" onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <SectionCard>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <Stack
                                direction={{xs: "column", sm: "row"}}
                                spacing={2}
                            >
                                <TextField
                                    name="name"
                                    required
                                    fullWidth
                                    label={
                                        translations?.adminCreateUserPageName ||
                                        "Ime"
                                    }
                                />
                                <TextField
                                    name="surname"
                                    required
                                    fullWidth
                                    label={
                                        translations?.adminCreateUserPageSurname ||
                                        "Prezime"
                                    }
                                />
                            </Stack>
                            <TextField
                                name="email"
                                type="email"
                                required
                                fullWidth
                                label={
                                    translations?.adminCreateUserPageEmail ||
                                    "Email"
                                }
                            />
                            <TextField
                                select
                                fullWidth
                                label={translations?.adminUsersPageRole || "Uloga"}
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                disabled={!owner}
                                helperText={
                                    !owner
                                        ? "Samo vlasnik može menjati ulogu novog korisnika."
                                        : type === "O"
                                        ? "Vlasnik može kreirati i deaktivirati ostale korisnike."
                                        : "Administrator može kreirati nove administratore."
                                }
                            >
                                <MenuItem value="A">
                                    {translations?.roleAdmin || "Administrator"}
                                </MenuItem>
                                {owner && (
                                    <MenuItem value="O">
                                        {translations?.roleOwner || "Vlasnik"}
                                    </MenuItem>
                                )}
                            </TextField>
                            <Button
                                type="submit"
                                fullWidth
                                size="large"
                                variant="contained"
                                disabled={mutation.isLoading}
                                startIcon={
                                    mutation.isLoading ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <PersonAddAlt1OutlinedIcon/>
                                    )
                                }
                                sx={{mt: 1}}
                            >
                                {translations?.adminCreateUserPageAddButton ||
                                    "Kreiraj korisnika"}
                            </Button>
                        </Stack>
                    </Box>
                </SectionCard>
            </Stack>
        </Box>
    );
};

export default CreateUserPage;